/* ═════════════════════════════════════════════════════════════════
   Public Pulse Verification Capsule
   ═════════════════════════════════════════════════════════════════
   Reachable at /pulse-verification/:token — no auth required. The
   customer sees a single-screen "Executive Task" with three
   sequential steps:
     1. Confirm Satisfaction (Yes/No)
     2. Clarify Repeated Trades (dropdown + text)
     3. Digital Signature + Sign & Submit
   After submit, an optional "Permanent Declaration Video" module lets
   them record a 60-second declaration with the Razorpay teleprompter.
   ═════════════════════════════════════════════════════════════════ */
import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast, Toaster } from "sonner";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShieldCheck, Check, X, Loader2, Video, Square, PenLine,
  Sparkles, BadgeCheck, RotateCcw, Send,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REPEATED_TRADE_REASONS = [
  "30/70 Price Lock installments for an active infrastructure project",
  "Milestone-based invoicing for a live construction dispatch",
  "Recurring bulk cement / TMT procurement",
  "Wallet-recharge remittances for ongoing trades",
  "Other (please specify below)",
];

export default function PulseVerification() {
  const { token } = useParams();
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);        // 1=form, 2=video, 3=done
  const [satisfied, setSatisfied] = useState(true);
  const [reasonKey, setReasonKey] = useState(REPEATED_TRADE_REASONS[0]);
  const [reasonText, setReasonText] = useState("");
  const [projectName, setProjectName] = useState("");
  const [busy, setBusy] = useState(false);
  const [signaturePng, setSignaturePng] = useState(null);
  const [audit, setAudit] = useState(null);
  const sigRef = useRef(null);
  const sigDrawingRef = useRef(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/public/pulse-verification/${token}`);
      setCtx(data);
      setProjectName(data.project_name || "");
      if (data.already_submitted) setStep(data.has_video ? 3 : 2);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Link invalid or expired");
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // ── Signature canvas ────────────────────────────────
  const initCanvas = () => {
    const c = sigRef.current;
    if (!c) return;
    const ctx2 = c.getContext("2d");
    ctx2.fillStyle = "#0A0906";
    ctx2.fillRect(0, 0, c.width, c.height);
    ctx2.strokeStyle = "#F6E4A6";
    ctx2.lineWidth = 2.4;
    ctx2.lineCap = "round";
  };
  useEffect(() => { if (step === 1) initCanvas(); }, [step]);

  const canvasPos = (e) => {
    const c = sigRef.current, r = c.getBoundingClientRect();
    const x = (e.touches?.[0]?.clientX ?? e.clientX) - r.left;
    const y = (e.touches?.[0]?.clientY ?? e.clientY) - r.top;
    return { x: (x / r.width) * c.width, y: (y / r.height) * c.height };
  };
  const startDraw = (e) => { sigDrawingRef.current = true; const { x, y } = canvasPos(e); const ctx2 = sigRef.current.getContext("2d"); ctx2.beginPath(); ctx2.moveTo(x, y); };
  const draw = (e) => { if (!sigDrawingRef.current) return; e.preventDefault(); const { x, y } = canvasPos(e); const ctx2 = sigRef.current.getContext("2d"); ctx2.lineTo(x, y); ctx2.stroke(); };
  const stopDraw = () => { sigDrawingRef.current = false; setSignaturePng(sigRef.current.toDataURL("image/png")); };
  const clearSig = () => { initCanvas(); setSignaturePng(null); };

  const submitForm = async () => {
    if (!signaturePng) { toast.error("Please sign inside the capsule"); return; }
    const reason = reasonKey.startsWith("Other")
      ? reasonText.trim()
      : (reasonText.trim() ? `${reasonKey} — ${reasonText.trim()}` : reasonKey);
    if (reason.length < 6) { toast.error("Please describe the trade reason"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/public/pulse-verification/${token}`, {
        satisfied, repeated_trade_reason: reason,
        project_name: projectName, signature_data_url: signaturePng,
      });
      toast.success("Verification submitted — thank you");
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#070505] text-[#D8B23E]">
        <Loader2 className="animate-spin" size={26} />
      </div>
    );
  }
  if (!ctx) return null;

  return (
    <div className="min-h-screen bg-[#070505] text-[#F5F5F0] relative overflow-hidden">
      <Toaster theme="dark" position="top-center" richColors />
      {/* Obsidian ambient */}
      <div className="absolute inset-0 opacity-40 pointer-events-none"
             style={{ backgroundImage:
               "radial-gradient(circle at 15% 20%, #B87333 0%, transparent 35%)," +
               "radial-gradient(circle at 85% 75%, #D8B23E 0%, transparent 35%)" }} />
      <div className="relative max-w-3xl mx-auto px-6 py-14">
        {/* Header shield */}
        <div className="flex items-center gap-4 mb-8">
          <VinciShield />
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#D8B23E]">Executive Mandate</div>
            <h1 className="font-serif text-3xl mt-1 text-[#F6E4A6]">Trade Pulse Verification</h1>
            <p className="text-[12px] text-[#8A8A82] italic mt-1">
              Vinci Executive Command · Compliance Capsule · Razorpay Ledger Pulse
            </p>
          </div>
        </div>

        {/* Partner ribbon */}
        <div className="rounded-sm border border-[#D8B23E]/40 p-4 mb-8"
               style={{ background: "linear-gradient(90deg,#0F0C05 0%,#1D1508 50%,#0F0C05 100%)" }}>
          <div className="text-[10px] tracking-[0.22em] uppercase text-[#8A6A1A]">Presented to</div>
          <div className="font-serif text-xl mt-1">{ctx.name}</div>
          <div className="text-[11px] text-[#8A8A82] mt-0.5 font-mono">
            {ctx.email}{ctx.vinci_code ? ` · ${ctx.vinci_code}` : ""}
          </div>
        </div>

        <StepTracker step={step} />

        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.section key="form"
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="mt-8 space-y-6">

              <div>
                <div className="text-[10px] tracking-[0.22em] uppercase text-[#D8B23E]">§1 Satisfaction Confirmation</div>
                <div className="mt-2 font-body text-[15px]">
                  I confirm that I am <b>100% satisfied</b> with the construction material services received from Vinci Inc.
                </div>
                <div className="mt-3 flex gap-2">
                  <button data-testid="pulse-satisfied-yes"
                            onClick={() => setSatisfied(true)}
                            className={`px-6 py-2 rounded-sm text-[11px] font-grotesk uppercase tracking-[0.22em] inline-flex items-center gap-1.5
                                        ${satisfied ? "bg-[#2FB999] text-black" : "border border-white/20 text-[#C9C9C2]"}`}>
                    <Check size={12} /> Yes — Satisfied
                  </button>
                  <button data-testid="pulse-satisfied-no"
                            onClick={() => setSatisfied(false)}
                            className={`px-6 py-2 rounded-sm text-[11px] font-grotesk uppercase tracking-[0.22em] inline-flex items-center gap-1.5
                                        ${!satisfied ? "bg-[#C0392B] text-white" : "border border-white/20 text-[#C9C9C2]"}`}>
                    <X size={12} /> No
                  </button>
                </div>
              </div>

              <div>
                <div className="text-[10px] tracking-[0.22em] uppercase text-[#D8B23E]">§2 Reason for Repeated Trades</div>
                <select data-testid="pulse-reason-select"
                          value={reasonKey} onChange={(e) => setReasonKey(e.target.value)}
                          className="mt-2 w-full bg-black/50 border border-white/20 rounded-sm px-3 py-2 text-[#F5F5F0]">
                  {REPEATED_TRADE_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <textarea data-testid="pulse-reason-text"
                            rows={2} value={reasonText} onChange={(e) => setReasonText(e.target.value)}
                            placeholder={reasonKey.startsWith("Other") ? "Please specify (required)" : "Optional — add project or dispatch details"}
                            className="mt-2 w-full bg-black/50 border border-white/20 rounded-sm px-3 py-2 text-[#F5F5F0]" />
                <input data-testid="pulse-project"
                         value={projectName} onChange={(e) => setProjectName(e.target.value)}
                         placeholder="Project name (e.g. Mumbai Metro Steel Link)"
                         className="mt-2 w-full bg-black/50 border border-white/20 rounded-sm px-3 py-2 text-[#F5F5F0]" />
              </div>

              <div>
                <div className="text-[10px] tracking-[0.22em] uppercase text-[#D8B23E] flex items-center gap-2">
                  <PenLine size={12} /> §3 Digital Pulse — Sign inside the capsule
                </div>
                <div className="mt-2 rounded-sm border border-[#D8B23E]/50 bg-[#0A0906] relative">
                  <canvas ref={sigRef} width={720} height={180}
                            onMouseDown={startDraw} onMouseMove={draw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
                            onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw}
                            data-testid="pulse-signature-canvas"
                            className="w-full h-32 touch-none cursor-crosshair" />
                  <button onClick={clearSig}
                            className="absolute top-2 right-2 text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] hover:text-[#D8B23E] flex items-center gap-1">
                    <RotateCcw size={10} /> Clear
                  </button>
                </div>
              </div>

              <div className="pt-4 border-t border-white/10 flex items-center justify-between flex-wrap gap-3">
                <p className="text-[11px] text-[#8A8A82] italic">
                  Submitting this dossier records a Razorpay-visible trail linking you and Vinci Executive Command.
                </p>
                <button data-testid="pulse-submit"
                          onClick={submitForm} disabled={busy}
                          className="text-[11px] font-grotesk uppercase tracking-[0.22em] text-black bg-gradient-to-r from-[#D8B23E] via-[#F6E4A6] to-[#B87333] px-6 py-2.5 rounded-sm inline-flex items-center gap-1.5">
                  {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                  Sign &amp; Submit
                </button>
              </div>
            </motion.section>
          )}

          {step === 2 && (
            <VideoDeclarationStep
              key="video"
              token={token} ctx={ctx}
              onDone={(a) => { setAudit(a); setStep(3); }} />
          )}

          {step === 3 && (
            <motion.section key="done"
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className="mt-8 space-y-6">
              <div className="rounded-sm border border-[#2FB999]/50 bg-[#2FB999]/5 p-6">
                <div className="flex items-center gap-3">
                  <BadgeCheck className="text-[#2FB999]" size={30} />
                  <div>
                    <div className="text-[10px] tracking-[0.22em] uppercase text-[#2FB999]">Compliance Authenticated</div>
                    <h2 className="font-serif text-2xl mt-1">Verification archived by Vinci Executive Command</h2>
                    <p className="text-[13px] text-[#C9C9C2] mt-1 max-w-2xl">
                      A formal record has been dispatched to the Vinci Trade Desk. This declaration is the <b>Lifelong Undertaking</b> — any further Razorpay questioning of these transactions can now be answered by this dossier.
                    </p>
                  </div>
                </div>
                {audit?.audit_link && (
                  <div className="mt-4 p-4 border border-[#D8B23E]/40 rounded-sm bg-black/40">
                    <div className="text-[10px] tracking-[0.22em] uppercase text-[#D8B23E]">Password-Protected Audit Link</div>
                    <div className="mt-1 font-mono text-sm text-[#F6E4A6] break-all">{audit.audit_link}</div>
                    <div className="mt-2 text-[11px] text-[#8A8A82]">
                      Password: <span className="font-mono text-[#F6E4A6]">{audit.audit_password}</span>
                    </div>
                  </div>
                )}
              </div>
            </motion.section>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

/* ── Step tracker ─────────────────────────────────────────────────── */
function StepTracker({ step }) {
  const steps = [{ n: 1, t: "Written Dossier" }, { n: 2, t: "Declaration Video" }, { n: 3, t: "Compliance Authenticated" }];
  return (
    <div className="flex items-center gap-3">
      {steps.map((s, i) => (
        <div key={s.n} className="flex items-center gap-3 flex-1">
          <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-grotesk
                              ${step >= s.n ? "bg-[#D8B23E] text-black" : "border border-white/20 text-[#8A8A82]"}`}>
            {step > s.n ? <Check size={12} /> : s.n}
          </div>
          <div className={`text-[10px] tracking-[0.22em] uppercase ${step >= s.n ? "text-[#F6E4A6]" : "text-[#8A8A82]"}`}>{s.t}</div>
          {i < steps.length - 1 && <div className="flex-1 h-px bg-white/10" />}
        </div>
      ))}
    </div>
  );
}

/* ── Video declaration with teleprompter ──────────────────────────── */
function VideoDeclarationStep({ token, ctx, onDone }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const rafRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const [blob, setBlob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [scrollY, setScrollY] = useState(0);

  const script = `I, ${ctx.name}, holder of ${ctx.vinci_code || "the Vinci Trade Code assigned to me"}, representing ${ctx.business_name || "my business"}, do hereby declare that I am 100% satisfied with the construction material services provided by Vinci Inc.

These repeated transactions are my official 30/70 Price Lock installments for active infrastructure projects.

I am recording this specifically for the Razorpay Risk & Compliance team. I request that Razorpay accept and archive this video as my Permanent Lifelong Declaration of Satisfaction. Any further questioning or disruption of my transactions based on these same parameters shall be considered unnecessary harassment of my business operations.`;

  const startRecord = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 1280, height: 720 }, audio: true });
      streamRef.current = stream;
      videoRef.current.srcObject = stream;
      await videoRef.current.play();

      const mr = new MediaRecorder(stream,
        { mimeType: MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
                       ? "video/webm;codecs=vp9,opus" : "video/webm" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = () => {
        const b = new Blob(chunksRef.current, { type: "video/webm" });
        setBlob(b);
        stream.getTracks().forEach((t) => t.stop());
        videoRef.current.srcObject = null;
        cancelAnimationFrame(rafRef.current);
      };
      recorderRef.current = mr;
      mr.start(500);
      setRecording(true);
      setScrollY(0);
      const start = performance.now();
      const tick = (now) => {
        setScrollY(Math.min(((now - start) / 60000) * 100, 100));
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch (e) {
      toast.error("Camera / microphone access required");
    }
  };

  const stopRecord = () => {
    recorderRef.current?.stop();
    setRecording(false);
  };

  const reset = () => { setBlob(null); setScrollY(0); };

  const upload = async () => {
    if (!blob) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", blob, `declaration-${token.slice(0, 6)}.webm`);
      const { data } = await axios.post(
        `${API}/public/pulse-verification/${token}/video`, form,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Declaration video archived");
      onDone(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="mt-8 space-y-4">
      <div>
        <div className="text-[10px] tracking-[0.22em] uppercase text-[#D8B23E] flex items-center gap-2">
          <Video size={12} /> Permanent Declaration Video
        </div>
        <p className="text-[13px] text-[#C9C9C2] mt-2 max-w-2xl">
          Read the teleprompter script aloud. This becomes your <b>Lifelong Undertaking</b> — permanently archived and shareable with Razorpay via a password-protected audit link.
        </p>
      </div>

      <div className="relative rounded-sm border border-[#D8B23E]/40 overflow-hidden bg-black">
        {blob ? (
          <video src={URL.createObjectURL(blob)} controls
                   data-testid="pulse-video-preview"
                   className="w-full max-h-[420px] bg-black" />
        ) : (
          <video ref={videoRef} muted playsInline
                   data-testid="pulse-video-live"
                   className="w-full max-h-[420px] bg-black" />
        )}

        {/* Biometric scan line + Vinci Code overlay while recording */}
        {recording && (
          <>
            <div className="absolute inset-x-0 h-1 bg-gradient-to-r from-transparent via-[#2FB999] to-transparent animate-pulse"
                   style={{ top: `${(scrollY * 4) % 100}%` }} />
            <div className="absolute top-3 left-3 px-3 py-1.5 rounded-sm border border-[#2FB999]/50 bg-black/70 text-[#2FB999] text-[10px] font-grotesk uppercase tracking-[0.22em] flex items-center gap-1.5">
              <ShieldCheck size={11} /> Vinci Identity Verified · {ctx.vinci_code || "VNC · PULSE"}
            </div>
            <div className="absolute bottom-3 right-3 px-3 py-1.5 rounded-sm bg-black/70 text-[#F6E4A6] text-[10px] font-mono">
              {new Date().toISOString().slice(0, 19).replace("T", " ")} UTC · REC
            </div>
          </>
        )}
      </div>

      {/* Teleprompter */}
      {!blob && (
        <div className="rounded-sm border border-[#D8B23E]/30 bg-black/60 p-5 max-h-56 overflow-hidden relative">
          <div className="text-[10px] tracking-[0.22em] uppercase text-[#8A6A1A] mb-2">Teleprompter · Razorpay Declaration</div>
          <div className="text-[15px] leading-relaxed text-[#F6E4A6] font-serif whitespace-pre-line transition-transform"
                 style={{ transform: recording ? `translateY(-${scrollY * 1.4}px)` : "translateY(0)" }}>
            {script}
          </div>
          <div className="pointer-events-none absolute bottom-0 inset-x-0 h-10 bg-gradient-to-t from-black to-transparent" />
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        {!recording && !blob && (
          <button data-testid="pulse-record-start"
                    onClick={startRecord}
                    className="text-[11px] font-grotesk uppercase tracking-[0.22em] text-black bg-gradient-to-r from-[#C0392B] to-[#E56B5F] px-5 py-2.5 rounded-sm inline-flex items-center gap-1.5">
            <Video size={12} /> Start Recording
          </button>
        )}
        {recording && (
          <button data-testid="pulse-record-stop"
                    onClick={stopRecord}
                    className="text-[11px] font-grotesk uppercase tracking-[0.22em] text-white bg-[#C0392B] px-5 py-2.5 rounded-sm inline-flex items-center gap-1.5">
            <Square size={12} /> Stop Recording
          </button>
        )}
        {blob && !busy && (
          <>
            <button data-testid="pulse-video-upload"
                      onClick={upload}
                      className="text-[11px] font-grotesk uppercase tracking-[0.22em] text-black bg-gradient-to-r from-[#2FB999] to-[#5FD3B7] px-5 py-2.5 rounded-sm inline-flex items-center gap-1.5">
              <Send size={12} /> Archive Declaration
            </button>
            <button onClick={reset}
                      className="text-[11px] font-grotesk uppercase tracking-[0.22em] border border-white/20 text-[#C9C9C2] px-5 py-2.5 rounded-sm inline-flex items-center gap-1.5">
              <RotateCcw size={12} /> Re-record
            </button>
          </>
        )}
        {busy && <Loader2 className="animate-spin text-[#D8B23E]" size={22} />}
      </div>

      <div className="pt-3 text-[11px] text-[#8A8A82] italic">
        Video ≤ 90 MB, up to about 60 seconds at 720p. This step is optional — the written verification is already recorded.
      </div>
    </motion.section>
  );
}

/* ── Vinci Shield ─────────────────────────────────────────────────── */
function VinciShield() {
  return (
    <svg width="58" height="58" viewBox="0 0 52 52" className="drop-shadow">
      <defs>
        <linearGradient id="ps-g" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0" stopColor="#F6E4A6" />
          <stop offset="0.5" stopColor="#D8B23E" />
          <stop offset="1" stopColor="#B87333" />
        </linearGradient>
      </defs>
      <circle cx="26" cy="26" r="24" stroke="url(#ps-g)" strokeWidth="1.4" fill="none" />
      <circle cx="26" cy="26" r="17" stroke="url(#ps-g)" strokeWidth="0.5" fill="none" opacity="0.6" />
      <text x="26" y="34" textAnchor="middle" fontSize="24" fontFamily="Times, serif" fontWeight="bold" fill="url(#ps-g)">V</text>
    </svg>
  );
}
